import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import type { ComponentProps } from 'react';
import { RequireFeature } from '@/features/authentication';

/** The action a route needs -- reaching a screen is VIEW; a page that only writes says so. */
type FeatureAction = NonNullable<ComponentProps<typeof RequireFeature>['action']>;
import { LazyLoad } from '@/shared/components/LazyLoad';
import { MastersLanding } from './common/menu/components/MastersLanding';
import { featureOfMaster } from './common/constants/masters.constants';
import type { MasterKey } from './common/constants/masters.constants';
/* The wizard and View, preloaded by the Supplier list so they open without the full-page spinner (2026-10-28). */
import { SupplierOnboardingFormPage, SupplierOnboardingViewPage } from './supplier/pages/supplierPageLoaders';

/**
 * The Masters route table, one entry per sub-feature.
 *
 * Every page is lazy so a master's chunk is fetched only when its route is
 * visited -- eighteen eagerly-imported pages would land in the main bundle.
 * `app/routes/protectedRoutes.tsx` composes this inside the shell.
 *
 * The imports are written out rather than built from MASTER_DEFINITIONS in a
 * loop: a bundler can only split on a literal `import()` path, so a computed
 * one would defeat the whole arrangement.
 *
 * Each route carries the FEATURE it leads to, resolved from the same registry
 * the sidebar and the screens read, so who may open it is decided by the RBAC
 * matrix rather than by the role slug the guard used to test.
 */
const OrganizationMasterPage = lazy(
  () => import('./organization/pages/OrganizationMasterPage'),
);
const CategoryMasterPage = lazy(() => import('./category/pages/CategoryMasterPage'));
const CountryMasterPage = lazy(() => import('./geography/country/pages/CountryMasterPage'));
const StateMasterPage = lazy(() => import('./geography/state/pages/StateMasterPage'));
const CityMasterPage = lazy(() => import('./geography/city/pages/CityMasterPage'));
const EntityMasterPage = lazy(() => import('./entity/pages/EntityMasterPage'));
const BusinessUnitMasterPage = lazy(
  () => import('./business-unit/pages/BusinessUnitMasterPage'),
);
const DivisionMasterPage = lazy(() => import('./division/pages/DivisionMasterPage'));
const DepartmentMasterPage = lazy(() => import('./department/pages/DepartmentMasterPage'));
const LocationMasterPage = lazy(() => import('./location/pages/LocationMasterPage'));
const UomMasterPage = lazy(() => import('./uom/pages/UomMasterPage'));
const MaterialMasterPage = lazy(() => import('./material/pages/MaterialMasterPage'));
const CurrencyMasterPage = lazy(() => import('./currency/pages/CurrencyMasterPage'));
const PaymentTermsMasterPage = lazy(
  () => import('./payment-terms/pages/PaymentTermsMasterPage'),
);
const DeliveryTermsMasterPage = lazy(
  () => import('./delivery-terms/pages/DeliveryTermsMasterPage'),
);
const IncotermMasterPage = lazy(() => import('./incoterm/pages/IncotermMasterPage'));
const SupplierMasterPage = lazy(() => import('./supplier/pages/SupplierMasterPage'));
/* Supplier onboarding from a template (NiraxoBid_SupplierFlow.html): the picker, the wizard and a supplier's view. */
const SupplierTemplatesPage = lazy(() => import('./supplier/pages/SupplierTemplatesPage'));
const DesignationMasterPage = lazy(() => import('./designation/pages/DesignationMasterPage'));
const UserManagementMasterPage = lazy(
  () => import('./user-management/pages/UserManagementMasterPage'),
);

/**
 * Keeps the route entries below to one line each, and is where a route becomes a feature.
 *
 * The key is the master's own key, so the feature comes from `MASTER_DEFINITIONS` -- the one registry
 * the sidebar filter and `MasterScreen` also read. A master with no feature mapped is left unguarded,
 * the same harmless direction those two take: the page opens and the platform still refuses whatever
 * the role may not do.
 */
function page(key: MasterKey, element: React.ReactNode, action?: FeatureAction): RouteObject['element'] {
  const guarded = <LazyLoad>{element}</LazyLoad>;
  const feature = featureOfMaster(key);

  return feature === undefined ? (
    guarded
  ) : (
    <RequireFeature feature={feature} {...(action ? { action } : {})}>
      {guarded}
    </RequireFeature>
  );
}

export const masterRoutes: RouteObject[] = [
  {
    path: 'masters',
    children: [
      /*
        `/masters` itself is not a page. Redirecting rather than rendering the
        first master at both URLs keeps the sidebar's NavLink matching one
        path.
      */
      { index: true, element: <MastersLanding /> },

      { path: 'organization', element: page('organization', <OrganizationMasterPage />) },
      { path: 'category', element: page('category', <CategoryMasterPage />) },
      { path: 'country', element: page('country', <CountryMasterPage />) },
      { path: 'state', element: page('state', <StateMasterPage />) },
      { path: 'city', element: page('city', <CityMasterPage />) },
      { path: 'entity', element: page('entity', <EntityMasterPage />) },
      { path: 'business-unit', element: page('business-unit', <BusinessUnitMasterPage />) },
      { path: 'division', element: page('division', <DivisionMasterPage />) },
      { path: 'department', element: page('department', <DepartmentMasterPage />) },
      { path: 'location', element: page('location', <LocationMasterPage />) },
      { path: 'uom', element: page('uom', <UomMasterPage />) },
      { path: 'material', element: page('material', <MaterialMasterPage />) },
      { path: 'currency', element: page('currency', <CurrencyMasterPage />) },
      { path: 'payment-terms', element: page('payment-terms', <PaymentTermsMasterPage />) },
      { path: 'delivery-terms', element: page('delivery-terms', <DeliveryTermsMasterPage />) },
      { path: 'incoterm', element: page('incoterm', <IncotermMasterPage />) },
      /* The Purchase Requests screen (NB-355): the same pages as the Purchase Requests menu, at its own route. */
      { path: 'pr', element: page('pr', <Navigate to={ROUTES.PURCHASE_REQUESTS} replace />) },
      { path: 'supplier', element: page('supplier', <SupplierMasterPage />) },
      { path: 'supplier/new', element: page('supplier', <SupplierTemplatesPage />, 'create') },
      {
        path: 'supplier/new/:templateId',
        element: page('supplier', <SupplierOnboardingFormPage mode="create" />, 'create'),
      },
      { path: 'supplier/:id', element: page('supplier', <SupplierOnboardingViewPage />) },
      {
        path: 'supplier/:id/edit',
        element: page('supplier', <SupplierOnboardingFormPage mode="edit" />, 'update'),
      },
      {
        path: 'supplier/:id/duplicate',
        element: page('supplier', <SupplierOnboardingFormPage mode="duplicate" />, 'create'),
      },
      { path: 'designation', element: page('designation', <DesignationMasterPage />) },
      { path: 'user-management', element: page('user-management', <UserManagementMasterPage />) },
    ],
  },
];
